import MXListProductCard from '@c/cards/MXListProductCard';
import MXProductCard from '@c/cards/MXProductCard';
import EmptyState from '@c/dashboard/EmptyState';
import ProductCardSkeleton, {
  ListProductCardSkeleton,
} from '@c/skeletons/ProductCardSkeleton';
import Button from '@ui/Button';
import Paginator from '@ui/Paginator';
import { logEvent } from '@util/analytics';
import useBikes from '@util/hooks/useBikes';
import { useFilterPills } from '@util/hooks/useFilterPills';
import useLogWhenInView from '@util/hooks/useLogWhenInView';
import useSearchId from '@util/hooks/useSearchId';
import { rankOf } from '@util/searchAttribution';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import type { Pagination } from '@util/types/paginator';
import { useBikeFilter } from 'context/BikeFilterContext';
import { MapPin, X } from 'lucide-react';
import type { OrderItemDocument } from 'models/order';
import type { ProductLight } from 'models/product';
import { usePathname } from 'next/navigation';
import { Fragment } from 'react';
import PageFilterPills from './PageFilterPills';

export default function PageMainContent({
  view,
  isFetching,
  products,
  pageInfo,
  filters,
  controls,
  full,
  hidePaginator,
  filterState,
  onFiltersChange,
  attribution,
  setIsFetching,
  clearAllFilters,
  searchControlsRef,
}: {
  view: 'grid' | 'list' | 'map';
  isFetching: boolean;
  products: ProductLight[];
  pageInfo: Pagination;
  filters: React.ReactNode;
  controls: React.ReactNode;
  full?: boolean;
  hidePaginator?: boolean;
  filterState: PageSideFilters;
  onFiltersChange: (filters: PageSideFilters) => void;
  attribution: OrderItemDocument['attribution'];
  setIsFetching?: (isFetching: boolean) => void;
  clearAllFilters: () => void;
  searchControlsRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const { bikeModels } = useBikes();
  const { clearSelectedBike } = useBikeFilter();
  const pathname = usePathname();
  const searchId = useSearchId();
  // each card reports which search showed it and where, with impressions and views
  const cardAttribution = (
    index: number
  ): OrderItemDocument['attribution'] => ({
    ...attribution,
    search_id: searchId,
    rank: rankOf(
      pageInfo?.page ?? 1,
      pageInfo?.itemsPerPage ?? products?.length ?? 0,
      index
    ),
  });

  const {
    pills,
    showClearAll,
    hasVisiblePills,
    handleClearAll,
    createBikeDetailsRemoveHandler,
  } = useFilterPills(
    filterState,
    (filter) => {
      onFiltersChange({
        ...filterState,
        [filter.key]: filter.value,
        ...(filter.key === 'bikeMakes' && {
          bikeModels: filterState.bikeModels?.filter((model) =>
            bikeModels.includes(model)
          ),
        }),
      });
    },
    clearAllFilters,
    clearSelectedBike
  );

  // An empty page with filters on is usually the filters' doing — a bike
  // picked earlier is easy to miss — so clearing them is offered first.
  const filtered = showClearAll || !!filterState.bikeModels?.length;
  const onProfile = !!pathname?.includes('profile');
  const noResults = (
    <div className="flex flex-col items-center">
      <div className="mb-5 text-center">
        <EmptyState
          icon={
            <img
              src="/no-listings.png"
              alt="No listings"
              className="h-[12rem] w-[12rem] object-contain"
            />
          }
          title={'No Results'}
          description={
            onProfile
              ? ''
              : filtered
              ? 'Your filters or selected vehicle may be hiding results.'
              : 'Have one to sell?'
          }
        />
      </div>

      {!onProfile &&
        (filtered ? (
          <Button
            text="Reset filters"
            type="primary"
            onClick={handleClearAll}
          />
        ) : (
          <Button text="Sell Yours" type="tertiary" href="/create-listing" />
        ))}
    </div>
  );

  const ref = useLogWhenInView(() =>
    logEvent('view_item_list', {
      item_list_id: `${pathname}`,
      item_list_name: `${pathname}`,
      item_category: `${pathname}`,
      items: products.map((p) => ({
        item_id: p.id,
        item_name: p.title,
      })),
    })
  );

  return (
    <div
      className={`flex w-full items-start gap-x-8 sm:mx-auto sm:max-w-content sm:py-16`}
    >
      {/* side filters*/}
      <div className="hidden lg:z-10 lg:block">{filters}</div>

      <div className={`w-full pb-[5rem] sm:mx-auto sm:max-w-content`} ref={ref}>
        <div
          ref={searchControlsRef || undefined}
          className="mb-8 mt-6 flex w-full gap-0 px-4 sm:mt-0 sm:px-0 lg:gap-[1.6rem]"
        >
          {controls}
        </div>
        {/* main content */}
        <div className="flex w-full gap-[2.2rem]">
          {/* controls */}
          {/* top filters and results */}
          <div className="flex h-full w-full flex-col lg:h-min">
            {hasVisiblePills && (
              <div className="mt-8 flex flex-col-reverse justify-between gap-[2.4rem] overflow-x-hidden px-8 pt-0 sm:px-0 lg:flex-row lg:flex-wrap lg:items-center lg:gap-0">
                <div className="flex w-full">
                  <PageFilterPills
                    filters={filterState}
                    handleFilterRemove={(filter) => {
                      onFiltersChange({
                        ...filterState,
                        [filter.key]: filter.value,
                        ...(filter.key === 'bikeMakes' && {
                          bikeModels: filterState.bikeModels?.filter((model) =>
                            bikeModels.includes(model)
                          ),
                        }),
                      });
                    }}
                    pills={pills}
                    showClearAll={showClearAll}
                    handleClearAll={handleClearAll}
                    createBikeDetailsRemoveHandler={
                      createBikeDetailsRemoveHandler
                    }
                  />
                </div>
              </div>
            )}

            {filterState.location && (
              <div className="flex justify-end px-8 sm:px-0">
                <button
                  type="button"
                  onClick={() =>
                    onFiltersChange({
                      ...filterState,
                      location: null,
                      radius: null,
                    })
                  }
                  className="flex items-center gap-[0.6rem] text-[1.5rem] font-semibold text-brand-secondary"
                >
                  <MapPin className="h-[1.6rem] w-[1.6rem]" />
                  {filterState.location.address}
                  {filterState.radius && (
                    <span className="text-zinc-400">
                      · {filterState.radius} mi
                    </span>
                  )}
                  <X className="h-[1.4rem] w-[1.4rem]" />
                </button>
              </div>
            )}

            <div className={`h-full w-full sm:mt-8`}>
              {view === 'grid' ? (
                // grid view
                <div className="mt-8 grid h-full w-full grid-cols-2 gap-x-[1.6rem] gap-y-[2rem] px-4 pb-[1.6rem] sm:mx-auto sm:mt-0 sm:max-w-default sm:justify-start sm:gap-x-[1.6rem] sm:gap-y-[0.25rem] sm:px-0 lg:grid-cols-[repeat(4,29.5rem)] lg:gap-x-[2rem] lg:gap-y-[5rem]">
                  {isFetching ? (
                    Array(20)
                      .fill(0)
                      .map((_, index) => <ProductCardSkeleton key={index} />)
                  ) : products?.length && products?.length > 0 ? (
                    products.map((result, index) => (
                      <MXProductCard
                        priority={index === 0}
                        product={result}
                        key={result.id + index}
                        attribution={cardAttribution(index)}
                      />
                    ))
                  ) : (
                    <div className="flex h-[60rem] w-[50rem] items-center justify-center sm:w-[120rem]">
                      {noResults}
                    </div>
                  )}
                </div>
              ) : view === 'list' ? (
                // list view
                <div className="flex h-full flex-col justify-center gap-[0.8rem] px-[2.5rem] pb-[1.6rem] sm:mt-[2.4rem] sm:gap-[1.6rem] sm:px-0">
                  {isFetching ? (
                    <>
                      {Array(20)
                        .fill(0)
                        .map((_, index) => (
                          <ListProductCardSkeleton key={index} />
                        ))}
                    </>
                  ) : products?.length ? (
                    products.map((result, index) => {
                      return (
                        <Fragment key={result.id}>
                          <MXListProductCard
                            product={result}
                            key={result.id}
                            attribution={cardAttribution(index)}
                          />
                          <hr />
                        </Fragment>
                      );
                    })
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      {noResults}
                    </div>
                  )}
                </div>
              ) : (
                // <DirtBikeMap />
                <></>
              )}
            </div>

            {pageInfo.totalPages > 1 && !hidePaginator && (
              <Paginator
                currentPage={pageInfo?.page ?? 1}
                totalPages={pageInfo?.totalPages ?? 0}
                scroll={true}
                setIsFetching={setIsFetching}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
